import React, { useState, useEffect, useCallback } from 'react';
import FileUpload from './components/FileUpload';
import EnhancedHealthDashboard from './components/EnhancedHealthDashboard';
import LeftSidebar from './components/LeftSidebar';
import TrendsView from './components/TrendsView';
import UserPanel from './components/UserPanel';
import ChangeLogs from './components/ChangeLogs';
import AssistantAgent from './components/AssistantAgent';
import apiFetch from './apiFetch';
import { handleSsoAutoLogin, getSsoUser } from './ssoAuth';
import './index.css';

const capitalizeWord = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Merge a growing list of per-site live results into one dashboard payload,
 * recomputing the combined overall stats. Used by the progressive site loader
 * so the view fills in as each site streams back from the backend.
 */
function mergeLiveSites(sites, client) {
  const renum = sites.map((s, i) => {
    const clean = (s.site_name || '').replace(/^Site \d+:\s*/, '');
    const label = `Site ${i + 1}: ${clean}`;
    const site = { ...s, site_name: label };
    if (site.site_stats) site.site_stats = { ...site.site_stats, site_name: label };
    return site;
  });
  const ratios = renum.map((s) => s.health_ratio).filter((v) => v !== null && v !== undefined);
  const overall_stats = {
    total_sites: renum.length,
    total_inverters: renum.reduce((sum, s) => sum + (s.inverter_details?.length || 0), 0),
    avg_health_ratio: ratios.length
      ? Math.round((ratios.reduce((a, b) => a + b, 0) / ratios.length) * 100) / 100
      : null,
    data_date: renum[0]?.site_stats?.data_date || null,
    unique_days: 1,
    is_multi_day: false,
  };
  const combined_hourly_data = renum.flatMap((s) => s.hourly_data || []);
  return { success: true, sites: renum, overall_stats, combined_hourly_data, daily_stats: [], client_name: client };
}

function App() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoadError, setHistoryLoadError] = useState(false);
  const [viewingHistoryData, setViewingHistoryData] = useState(null);
  const [viewingHistoryInfo, setViewingHistoryInfo] = useState(null);
  const [loadingHistoryEntry, setLoadingHistoryEntry] = useState(null); // id of entry being loaded
  const [hasCheckedLatest, setHasCheckedLatest] = useState(false);
  const [latestFileInfo, setLatestFileInfo] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showHeaderTools, setShowHeaderTools] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('voltiq_dark_mode') === '1'; } catch { return false; }
  });

  // Persist dark-mode choice across sessions (standardized across the AGS Astra suite)
  useEffect(() => {
    try { localStorage.setItem('voltiq_dark_mode', darkMode ? '1' : '0'); } catch { /* ignore */ }
  }, [darkMode]);
  const [dataIsSummary, setDataIsSummary] = useState(false);
  const [loadingFullData, setLoadingFullData] = useState(false);
  const [liveProgress, setLiveProgress] = useState(null); // {client, loaded, total} while streaming live sites

  // Astra SSO gate: when REACT_APP_REQUIRE_SSO=true (production), VoltIQ only
  // opens if launched from the Astra portal (valid SSO session). Locally it's
  // unset, so the gate is off and the app runs normally.
  const requireSso = process.env.REACT_APP_REQUIRE_SSO === 'true';
  const [ssoUser, setSsoUser] = useState(() => getSsoUser());
  const [ssoChecked, setSsoChecked] = useState(false);

  // SSO auto-login: if launched from the AGS Suite with a suite_token, verify it
  // and persist the session (handled inside ssoAuth). VoltIQ has no UI login of
  // its own, so the result isn't displayed — the handshake just runs silently.
  useEffect(() => {
    let mounted = true;
    (async () => {
      await handleSsoAutoLogin();
      if (mounted) {
        setSsoUser(getSsoUser());
        setSsoChecked(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Initial load: use lightweight summary so Overview appears quickly (important for large uploads)
  useEffect(() => {
    const loadLatestResults = async () => {
      try {
        setLoading(true);
        const response = await apiFetch(`/latest?summary=1`);
        
        if (!response.ok) {
          setLoading(false);
          return;
        }
        
        const result = await response.json();
        
        if (result.success && result.result) {
          setData(result.result);
          setError(null);
          setDataIsSummary(true);
          setLatestFileInfo({
            filename: result.filename || 'Unknown',
            client_name: result.client_name || null,
            uploaded_at: result.uploaded_at || null
          });
        } else {
          setLatestFileInfo(null);
        }
        setHasCheckedLatest(true);
      } catch (err) {
        console.error('Error loading latest results:', err);
        setHasCheckedLatest(true);
      } finally {
        setLoading(false);
      }
    };

    loadLatestResults();
  }, []);

  // Load full dataset when user opens Analytics (needed for detailed tables and charts)
  const loadFullData = useCallback(async () => {
    if (loadingFullData || !dataIsSummary) return;
    try {
      setLoadingFullData(true);
      const response = await apiFetch(`/latest`);
      if (!response.ok) return;
      const result = await response.json();
      if (result.success && result.result) {
        setData(result.result);
        setDataIsSummary(false);
      }
    } catch (err) {
      console.error('Error loading full results:', err);
    } finally {
      setLoadingFullData(false);
    }
  }, [dataIsSummary, loadingFullData]);

  // Load full dataset when viewing Analytics / detailed analysis (needed for detailed tables and charts)
  useEffect(() => {
    // Overview/dashboard deliberately stay on the summary: the full payload runs to
    // several MB and most dashboard visits never need it (phone-first users). The All
    // Sites Combined table asks for it on demand instead — clicking a cell triggers
    // loadFullData and the modal opens as soon as the rows land.
    const needsFullData = ['analysis', 'analysis-site', 'analysis-inverter', 'analytics'].includes(activeSection);
    if (needsFullData && data?.sites?.length > 0 && dataIsSummary) {
      loadFullData();
    }
  }, [activeSection, data?.sites?.length, dataIsSummary, loadFullData]);

  const loadHistory = useCallback(async () => {
    const HISTORY_TIMEOUT_MS = 15000; // 15s — slim list is small but cold Cloud Run start can be slow
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HISTORY_TIMEOUT_MS);
    try {
      setLoadingHistory(true);
      setHistoryLoadError(false);
      const response = await apiFetch(`/history`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) {
        setUploadHistory([]);
        setHistoryLoadError(true);
        return;
      }
      const result = await response.json();
      if (result.success) {
        setUploadHistory(result.history || []);
        setHistoryLoadError(false);
      } else {
        setUploadHistory([]);
        setHistoryLoadError(true);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        console.warn('History request timed out');
      } else {
        console.error('Error loading history:', err);
      }
      setUploadHistory([]);
      setHistoryLoadError(true);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'history') {
      loadHistory();
    }
  }, [activeSection, loadHistory]);

  const handleDataProcessed = (processedData) => {
    setData(processedData);
    setDataIsSummary(false); // upload returns full data
    setError(null);
    setActiveSection('overview');
    setViewingHistoryData(null);
    setViewingHistoryInfo(null);
    if (processedData.filename || processedData.client_name) {
      setLatestFileInfo({
        filename: processedData.filename || 'Unknown',
        client_name: processedData.client_name || null,
        uploaded_at: processedData.uploaded_at || null
      });
    }
  };

  // Progressive live fetch: load the first site fast, then stream the rest in
  // the background and merge them in as they arrive (site-picker behavior).
  const handleFetchLive = useCallback(async (client) => {
    const MAX_SITES = 200;   // covers Ameresco's ~164 sites
    const CONCURRENCY = 4;   // concurrent per-site fetches (backend also parallelizes inverters)
    setError(null);
    setLiveProgress({ client, loaded: 0, total: null });
    try {
      const listResp = await apiFetch(`/live/${encodeURIComponent(client)}/sites`);
      if (!listResp.ok) throw new Error(`Could not list sites (${listResp.status})`);
      const listData = await listResp.json();
      const targets = (listData.sites || []).slice(0, MAX_SITES);
      if (targets.length === 0) throw new Error('No sites available for this client');
      setLiveProgress({ client, loaded: 0, total: targets.length });

      const acc = [];
      let next = 0, done = 0, shown = false;
      const worker = async () => {
        while (next < targets.length) {
          const i = next++;
          try {
            const r = await apiFetch(`/live/${encodeURIComponent(client)}?site=${encodeURIComponent(targets[i].id)}`);
            if (r.ok) {
              const res = await r.json();
              if (res.success && res.sites?.length) {
                acc.push(...res.sites);
                setData(mergeLiveSites([...acc], client));
                setDataIsSummary(false);
                if (!shown) {
                  // First site in — jump to the dashboard so the user can watch.
                  shown = true;
                  setActiveSection('overview');
                  setViewingHistoryData(null);
                  setViewingHistoryInfo(null);
                  setLatestFileInfo({ filename: `${capitalizeWord(client)} Live`, client_name: client, uploaded_at: new Date().toISOString() });
                }
              }
            }
          } catch (e) {
            // Skip a failed/empty site and keep streaming the rest.
            console.warn('Live site failed:', targets[i]?.name, e);
          }
          done++;
          setLiveProgress({ client, loaded: done, total: targets.length });
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker));

      if (acc.length === 0) {
        setError('No live data returned for any site.');
      } else {
        // Persist the completed live run so it lands in history and survives reload.
        try {
          const fname = `${capitalizeWord(client)} Live — ${new Date().toLocaleString()}`;
          await apiFetch('/live/save', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ result: mergeLiveSites([...acc], client), client_name: client, filename: fname }),
          });
          setLatestFileInfo({ filename: fname, client_name: client, uploaded_at: new Date().toISOString() });
        } catch (e) {
          console.warn('Failed to save live run to history:', e);
        }
      }
    } catch (e) {
      setError('Live fetch error: ' + (e.message || 'unknown'));
    } finally {
      setLiveProgress(null);
    }
  }, []);

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleLoading = (isLoading) => {
    setLoading(isLoading);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setMobileSidebarOpen(false);
    if (section !== 'history') {
      setViewingHistoryData(null);
      setViewingHistoryInfo(null);
    }
  };

  const handleViewHistoryEntry = async (entry) => {
    // If the entry already has a result (older API or pre-loaded), use it
    if (entry.result) {
      setViewingHistoryData(entry.result);
      setViewingHistoryInfo({
        filename: entry.filename,
        client_name: entry.client_name,
        uploaded_at: entry.uploaded_at
      });
      return;
    }

    // Otherwise fetch the full entry on demand from /history/{id}
    try {
      setLoadingHistoryEntry(entry.id);
      const response = await apiFetch(`/history/${entry.id}`);
      if (!response.ok) {
        console.error('Failed to load history entry:', response.status);
        setLoadingHistoryEntry(null);
        return;
      }
      const result = await response.json();
      if (result.success && result.entry?.result) {
        setViewingHistoryData(result.entry.result);
        setViewingHistoryInfo({
          filename: result.entry.filename,
          client_name: result.entry.client_name,
          uploaded_at: result.entry.uploaded_at
        });
      }
    } catch (err) {
      console.error('Error loading history entry:', err);
    } finally {
      setLoadingHistoryEntry(null);
    }
  };

  const handleBackToHistoryList = () => {
    setViewingHistoryData(null);
    setViewingHistoryInfo(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const renderContent = () => {
    if (viewingHistoryData) {
  return (
        <>
          <div className="mb-6">
            <button
              onClick={handleBackToHistoryList}
              className="inline-flex items-center text-green-600 hover:text-green-700 font-medium transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to History
            </button>
          </div>

          {viewingHistoryInfo && (
            <div className={`mb-6 rounded-lg shadow-sm p-5 border-l-4 border-green-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Viewing Historical Data</p>
              <div className="flex items-center gap-3 flex-wrap">
                {viewingHistoryInfo.client_name && (
                  <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${darkMode ? 'text-green-400 bg-green-900' : 'text-green-700 bg-green-100'}`}>
                    {viewingHistoryInfo.client_name}
                  </span>
                )}
                <span className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{viewingHistoryInfo.filename}</span>
              </div>
            </div>
          )}

          <EnhancedHealthDashboard data={viewingHistoryData} darkMode={darkMode} />
        </>
      );
    }

    switch (activeSection) {
      case 'overview':
      case 'dashboard':
      case 'analysis':
      case 'analysis-site':
      case 'analysis-inverter':
        return (
          <>
            {loading && (
              <div className="flex flex-col justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Loading data...</span>
              </div>
            )}

            {error && (
              <div className={`border rounded-lg p-4 mb-6 ${darkMode ? 'bg-red-900 bg-opacity-30 border-red-800' : 'bg-red-50 border-red-200'}`}>
                <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-700'}`}>{error}</p>
              </div>
            )}

            {data && !loading && (
              <>
                {latestFileInfo && (
                  <div className={`mb-6 rounded-lg shadow-sm p-5 border-l-4 border-green-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Currently Viewing</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      {latestFileInfo.client_name && (
                        <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${darkMode ? 'text-green-400 bg-green-900' : 'text-green-700 bg-green-100'}`}>
                          {latestFileInfo.client_name}
                        </span>
                      )}
                      <span className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{latestFileInfo.filename}</span>
                    </div>
                    {latestFileInfo.uploaded_at && (
                      <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Uploaded: {formatDate(latestFileInfo.uploaded_at)}</p>
                    )}
                  </div>
                )}
                <EnhancedHealthDashboard
                  data={data}
                  darkMode={darkMode}
                  detailsPending={loadingFullData}
                  onRequestFullData={loadFullData}
                />
              </>
            )}

            {!data && !loading && hasCheckedLatest && (
              <div className={`flex flex-col items-center justify-center py-20 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <svg className={`w-10 h-10 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>No Data Available</h3>
                <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Upload an Excel file to get started</p>
                <button
                  onClick={() => setActiveSection('upload')}
                  className="px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  Upload Excel File
                </button>
              </div>
            )}
          </>
        );

      case 'history':
        return (
          <div className={`rounded-lg shadow-sm overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Upload History</h2>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last 10 uploads</p>
        </div>
            
            {loadingHistory ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
              </div>
            ) : uploadHistory.length === 0 ? (
              <div className="text-center py-16">
                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                  {historyLoadError ? 'Couldn\'t load history. Switch away and back to History to retry.' : 'No upload history available.'}
                </p>
              </div>
            ) : (
              <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {uploadHistory.map((entry) => (
                  <div key={entry.id} className={`p-5 transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          {entry.client_name && (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase ${darkMode ? 'text-green-400 bg-green-900' : 'text-green-700 bg-green-100'}`}>
                              {entry.client_name}
                            </span>
                          )}
                          <span className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{entry.filename}</span>
                        </div>
                        <div className={`flex items-center gap-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span>{formatDate(entry.uploaded_at)}</span>
                          {entry.total_sites && <span>{entry.total_sites} sites</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewHistoryEntry(entry)}
                        disabled={loadingHistoryEntry === entry.id}
                        className="px-5 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-wait transition-colors flex-shrink-0 inline-flex items-center gap-2"
                      >
                        {loadingHistoryEntry === entry.id ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                            Loading
                          </>
                        ) : 'View'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'upload':
        return (
          <div className={`rounded-lg shadow-sm p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Data Sources</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Fetch live inverter data or upload an Excel file to analyze</p>
                    <FileUpload
                      onDataProcessed={handleDataProcessed}
                      onError={handleError}
                      onLoading={handleLoading}
                      onFetchLive={handleFetchLive}
                      liveProgress={liveProgress}
                      darkMode={darkMode}
                    />
                  </div>
        );

      case 'inverters':
      case 'inverters-health':
        return (
          <div className="space-y-6">
            <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Inverter Health Status</h2>
              <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Overview of all inverter health across sites</p>
              
              {data && data.sites ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className={`rounded-lg p-4 border ${darkMode ? 'bg-green-900 bg-opacity-30 border-green-800' : 'bg-green-50 border-green-100'}`}>
                      <p className={`text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>Total Inverters</p>
                      <p className={`text-3xl font-bold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                        {data.sites.reduce((sum, site) => sum + (site.inverter_details?.length || 0), 0)}
                      </p>
                    </div>
                    <div className={`rounded-lg p-4 border ${darkMode ? 'bg-blue-900 bg-opacity-30 border-blue-800' : 'bg-blue-50 border-blue-100'}`}>
                      <p className={`text-sm font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Active Sites</p>
                      <p className={`text-3xl font-bold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>{data.sites.length}</p>
                    </div>
                    <div className={`rounded-lg p-4 border ${darkMode ? 'bg-yellow-900 bg-opacity-30 border-yellow-800' : 'bg-yellow-50 border-yellow-100'}`}>
                      <p className={`text-sm font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Avg Health Ratio</p>
                      <p className={`text-3xl font-bold ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                        {data.overall_stats?.avg_health_ratio?.toFixed(1) || 'N/A'}%
                      </p>
                    </div>
                    <div className={`rounded-lg p-4 border ${darkMode ? 'bg-purple-900 bg-opacity-30 border-purple-800' : 'bg-purple-50 border-purple-100'}`}>
                      <p className={`text-sm font-medium ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{data.overall_stats?.is_multi_day ? 'Data period (historical)' : 'Data Date'}</p>
                      <p className={`text-2xl font-bold ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>{data.overall_stats?.data_date || 'N/A'}</p>
                      {data.overall_stats?.is_multi_day && data.overall_stats?.unique_days > 1 && (
                        <p className={`text-xs mt-1 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{data.overall_stats.unique_days} days</p>
                      )}
                    </div>
                  </div>

                  {/* Inverter List by Site */}
                  <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Inverters by Site</h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {data.sites.slice(0, 10).map((site, idx) => (
                      <div key={idx} className={`border rounded-lg p-4 ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{site.site_name}</p>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {site.inverter_details?.length || 0} inverters • 
                              Health: {site.health_ratio?.toFixed(1) || 'N/A'}%
                            </p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                            (site.health_ratio || 0) >= 90 ? (darkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-700') :
                            (site.health_ratio || 0) >= 70 ? (darkMode ? 'bg-yellow-900 text-yellow-400' : 'bg-yellow-100 text-yellow-700') :
                            (darkMode ? 'bg-red-900 text-red-400' : 'bg-red-100 text-red-700')
                          }`}>
                            {(site.health_ratio || 0) >= 90 ? 'Healthy' :
                             (site.health_ratio || 0) >= 70 ? 'Fair' : 'Needs Attention'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No data available. Please upload a file first.</p>
                  <button onClick={() => setActiveSection('upload')} className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                    Go to Upload
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      case 'inverters-compare':
        return (
          <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Compare Inverters</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Compare inverter performance across different sites</p>
            
            {data && data.sites ? (
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Site</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Inverters</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Health Ratio</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Status</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {data.sites.slice(0, 15).map((site, idx) => (
                      <tr key={idx} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{site.site_name}</td>
                        <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{site.inverter_details?.length || 0}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center">
                            <div className={`w-16 rounded-full h-2 mr-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <div 
                                className={`h-2 rounded-full ${
                                  (site.health_ratio || 0) >= 90 ? 'bg-green-500' :
                                  (site.health_ratio || 0) >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(site.health_ratio || 0, 100)}%` }}
                              ></div>
                            </div>
                            <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{site.health_ratio?.toFixed(1) || 'N/A'}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            (site.health_ratio || 0) >= 90 ? (darkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-700') :
                            (site.health_ratio || 0) >= 70 ? (darkMode ? 'bg-yellow-900 text-yellow-400' : 'bg-yellow-100 text-yellow-700') :
                            (darkMode ? 'bg-red-900 text-red-400' : 'bg-red-100 text-red-700')
                          }`}>
                            {(site.health_ratio || 0) >= 90 ? 'Good' :
                             (site.health_ratio || 0) >= 70 ? 'Fair' : 'Poor'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No data available. Please upload a file first.</p>
              </div>
            )}
          </div>
        );

      case 'analytics':
      case 'analytics-hourly':
        // Analytics tab: same as Overview — full dashboard with site selector, day filter, real hourly chart & table
        return (
          <>
            {loading && (
              <div className="flex flex-col justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Loading data...</span>
              </div>
            )}
            {error && (
              <div className={`border rounded-lg p-4 mb-6 ${darkMode ? 'bg-red-900 bg-opacity-30 border-red-800' : 'bg-red-50 border-red-200'}`}>
                <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-700'}`}>{error}</p>
              </div>
            )}
            {data && !loading && (
              <>
                {latestFileInfo && (
                  <div className={`mb-6 rounded-lg shadow-sm p-5 border-l-4 border-green-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Currently Viewing</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      {latestFileInfo.client_name && (
                        <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${darkMode ? 'text-green-400 bg-green-900' : 'text-green-700 bg-green-100'}`}>
                          {latestFileInfo.client_name}
                        </span>
                      )}
                      <span className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{latestFileInfo.filename}</span>
                    </div>
                    {latestFileInfo.uploaded_at && (
                      <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Uploaded: {formatDate(latestFileInfo.uploaded_at)}</p>
                    )}
                  </div>
                )}
                <EnhancedHealthDashboard
                  data={data}
                  darkMode={darkMode}
                  detailsPending={loadingFullData}
                  onRequestFullData={loadFullData}
                />
              </>
            )}
            {!data && !loading && hasCheckedLatest && (
              <div className={`flex flex-col items-center justify-center py-20 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No data available. Upload a file from Upload, or open Overview first.</p>
                <button
                  onClick={() => setActiveSection('upload')}
                  className="mt-4 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700"
                >
                  Go to Upload
                </button>
              </div>
            )}
          </>
        );

      case 'analytics-trends':
        return (
          <TrendsView data={data} darkMode={darkMode} />
        );

      case 'sites':
      case 'sites-all':
        return (
          <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>All Sites</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Complete list of monitored solar sites</p>
            
            {data && data.sites ? (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Showing {data.sites.length} sites</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.sites.map((site, idx) => (
                    <div key={idx} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200'}`}>
                      <h3 className={`font-medium mb-2 truncate ${darkMode ? 'text-white' : 'text-gray-800'}`} title={site.site_name}>{site.site_name}</h3>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Inverters:</span>
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{site.inverter_details?.length || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Health:</span>
                          <span className={`font-medium ${
                            (site.health_ratio || 0) >= 90 ? (darkMode ? 'text-green-400' : 'text-green-600') :
                            (site.health_ratio || 0) >= 70 ? (darkMode ? 'text-yellow-400' : 'text-yellow-600') : (darkMode ? 'text-red-400' : 'text-red-600')
                          }`}>{site.health_ratio?.toFixed(1) || 'N/A'}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No data available. Please upload a file first.</p>
              </div>
            )}
          </div>
        );

      case 'sites-alerts':
        return (
          <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Sites with Alerts</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Sites that may need attention based on health ratio</p>
            
            {data && data.sites ? (
              <>
                {(() => {
                  const alertSites = data.sites.filter(s => (s.health_ratio || 0) < 85);
                  return alertSites.length > 0 ? (
                    <div className="space-y-3">
                      {alertSites.map((site, idx) => (
                        <div key={idx} className={`border-l-4 rounded-lg p-4 ${
                          (site.health_ratio || 0) < 70 
                            ? (darkMode ? 'border-red-500 bg-red-900 bg-opacity-30' : 'border-red-500 bg-red-50') 
                            : (darkMode ? 'border-yellow-500 bg-yellow-900 bg-opacity-30' : 'border-yellow-500 bg-yellow-50')
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{site.site_name}</h3>
                              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {site.inverter_details?.length || 0} inverters • Health: {site.health_ratio?.toFixed(1)}%
                              </p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                              (site.health_ratio || 0) < 70 
                                ? (darkMode ? 'bg-red-900 text-red-400' : 'bg-red-100 text-red-700') 
                                : (darkMode ? 'bg-yellow-900 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                            }`}>
                              {(site.health_ratio || 0) < 70 ? 'Critical' : 'Warning'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-12 rounded-lg ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
                      <svg className="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>All sites are performing well!</p>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-green-500' : 'text-green-600'}`}>No alerts at this time</p>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="text-center py-12">
                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No data available. Please upload a file first.</p>
              </div>
            )}
          </div>
        );

      case 'reports':
      case 'reports-daily':
        return (
          <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Daily Summary Report</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Overview of today's inverter performance</p>
            
            {data && data.sites ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`text-center p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{data.sites.length}</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Sites</p>
                  </div>
                  <div className={`text-center p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {data.sites.reduce((sum, site) => sum + (site.inverter_details?.length || 0), 0)}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Inverters</p>
                  </div>
                  <div className={`text-center p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`text-3xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                      {data.sites.filter(s => (s.health_ratio || 0) >= 90).length}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Healthy Sites</p>
                  </div>
                  <div className={`text-center p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`text-3xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                      {data.sites.filter(s => (s.health_ratio || 0) < 70).length}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Need Attention</p>
                  </div>
                </div>

                {/* Report Info */}
                <div className={`border rounded-lg p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Report Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Data Date:</span>
                      <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{data.overall_stats?.data_date || 'N/A'}</span>
                    </div>
                    <div>
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Avg Health Ratio:</span>
                      <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{data.overall_stats?.avg_health_ratio?.toFixed(2) || 'N/A'}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No data available. Please upload a file first.</p>
            </div>
          )}
          </div>
        );

      case 'reports-export':
        return (
          <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Export Data</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Download reports and data exports</p>
            
            <div className="space-y-4">
              <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}>
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${darkMode ? 'bg-green-900' : 'bg-green-100'}`}>
                    <svg className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>Full Report (PDF)</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Complete analysis with charts</p>
                  </div>
                </div>
              </div>
              
              <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}>
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                    <svg className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  </div>
                  <div>
                    <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>Raw Data (CSV)</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Spreadsheet format for analysis</p>
                  </div>
                </div>
              </div>
              
              <p className={`text-sm text-center mt-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Export functionality coming soon</p>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className={`rounded-lg shadow-sm p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Settings</h2>
            <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Configure application preferences</p>
            
            <div className="space-y-6">
              <div className={`border-b pb-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Display Settings</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Theme</span>
                    <button 
                      onClick={() => setDarkMode(!darkMode)}
                      className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                        darkMode 
                          ? 'bg-yellow-600 text-white hover:bg-yellow-500' 
                          : 'bg-gray-700 text-white hover:bg-gray-600'
                      }`}
                    >
                      {darkMode ? 'Switch to Light' : 'Switch to Dark'}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Health threshold (Good)</span>
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>≥ 90%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Health threshold (Fair)</span>
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>≥ 70%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Analysis hours</span>
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>7 AM - 6 PM</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className={`font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>About</h3>
                <div className={`text-sm space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <p>AGS VoltIQ - Inverter Health Monitor</p>
                  <p>Version 1.0.0</p>
                  <p className={darkMode ? 'text-gray-500' : 'text-gray-400'}>© 2025 American Green Solutions</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'changelogs':
        return <ChangeLogs darkMode={darkMode} />;

      default:
        return null;
    }
  };

  // SSO gate (production only). While verifying, show a spinner; if no valid
  // Astra session, block access entirely.
  if (requireSso && !ssoChecked) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
        <p className="text-gray-500">Verifying access…</p>
      </div>
    );
  }

  if (requireSso && !ssoUser) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100 p-6">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center border-t-4 border-green-500">
          <img src="/ags-log-nob.png" alt="American Green Solutions" className="h-14 w-auto mx-auto mb-6 object-contain" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">AGS VoltIQ</h1>
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto my-4">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Sign in through AGS Astra</h2>
          <p className="text-gray-500 mb-6">
            VoltIQ can only be opened from the AGS Astra portal. Please launch it from your Astra dashboard.
          </p>
          <a
            href="https://ags-astra.amgsol.com"
            className="inline-block px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            Go to AGS Astra
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar - hidden on mobile unless toggled */}
      <div className={`${mobileSidebarOpen ? 'fixed inset-y-0 left-0 z-50' : 'hidden'} md:relative md:flex md:z-auto`}>
        <LeftSidebar
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          darkMode={darkMode}
        />
      </div>

      {/* Standardized right-side Settings panel (logo / sidebar gear opens this).
          VoltIQ has no login of its own, so no identity/logout is shown here —
          SSO sign-in (when launched from the Suite) still works untouched. */}
      <UserPanel
        isOpen={showHeaderTools}
        onClose={() => setShowHeaderTools(false)}
        appName="AGS VoltIQ"
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        onOpenChangeLogs={() => setActiveSection('changelogs')}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Header — standardized 96px green bar (hamburger on mobile, centered title, logo opens panel) */}
        <header className="h-16 lg:h-24 flex-shrink-0 flex items-center px-4 lg:px-6" style={{ backgroundColor: '#86bb46' }}>
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/20 mr-2 flex-shrink-0"
            aria-label="Open menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Left spacer (desktop only — keeps title visually centered) */}
          <div className="hidden lg:block w-48 flex-shrink-0"></div>

          {/* Title + subtitle */}
          <div className="flex-1 flex justify-center">
            <div className="text-center">
              <h1 className="text-xl lg:text-3xl font-bold text-white tracking-wide">
                AGS VoltIQ
              </h1>
              <p className="text-xs lg:text-sm text-white/90 mt-0.5 hidden sm:block">
                Smarter inverter analytics
              </p>
            </div>
          </div>

          {/* Logo — tap to open user panel */}
          <div className="w-auto lg:w-48 flex items-center justify-end flex-shrink-0">
            <button
              onClick={() => setShowHeaderTools(true)}
              className="p-0 border-0 bg-transparent cursor-pointer hover:opacity-90 transition-opacity"
              aria-label="Open account panel"
              title="Account"
            >
              <img
                src="/ags-log-nob.png"
                alt="American Green Solutions"
                className="h-11 lg:h-16 w-auto max-h-11 lg:max-h-16 object-contain"
              />
            </button>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-3 md:p-6 overflow-y-auto">
          {liveProgress && liveProgress.total > 0 && (
            <div className={`mb-4 rounded-lg p-3 flex items-center gap-3 border-l-4 border-green-500 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 flex-shrink-0"></div>
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Loading {capitalizeWord(liveProgress.client)} live data — {liveProgress.loaded} of {liveProgress.total} sites… (you can explore loaded sites now)
              </span>
            </div>
          )}
          {renderContent()}
        </main>
      </div>

      {/* Floating AI assistant (frontend-only — answers from loaded data) */}
      <AssistantAgent data={data} />
    </div>
  );
}

export default App;
